// Scoreboard page - displays rankings and awards for members
// Shows "Best organizer" and "Best participant" scoreboards with points and achievements
// Also includes KL rating and awards display

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore'; // Firebase database
import { getAuth } from 'firebase/auth';
import { app } from './firebase-config'; // Firebase configuration
import KlRating from './KlRating'; // KL food event ratings component
import Utmerkelser from './Utmerkelser'; // Awards/achievements component
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Component that displays a single row in the scoreboard table
// Shows member name, points, and events they organized/attended
const UserRow = ({
  user, // The member's data
  eventsList, // List of all events
  expandedEventsUser, // Which user's events are expanded
  setExpandedEventsUser, // Function to toggle events expansion
  mode // Either 'arrangør' (organizer) or 'deltaker' (participant)
}) => {
  // Determine what data to show based on the mode (organizer vs participant)
  const isHost = mode === 'arrangør'; // True if showing organizers, false if showing participants
  const pointField = isHost ? user.host_points : user.points; // Points for organizing or participating
  const eventsField = isHost ? user.hosted_events : user.attended_events; // Events organized or attended

  // Function to toggle showing/hiding events
  const handleEventToggle = () => {
    const isCurrentlyExpanded = expandedEventsUser === user.id;
    setExpandedEventsUser(isCurrentlyExpanded ? null : user.id);
  };

  return (
    <tr>
      <td><Link to={`/profil/${user.id}`} className="user-link">{user.name}</Link></td>
      <td>{pointField || 0}</td>

      {/* Column for events list */}
      <td>
        {Array.isArray(eventsField) && eventsField.length > 0 ? (
          <>
            {/* Button to show/hide events list */}
            <button
              className={`button-17${expandedEventsUser === user.id ? '' : ' muted'}`}
              onClick={handleEventToggle}
            >
              {expandedEventsUser === user.id
                ? 'Skjul events'
                : `Vis ${eventsField.length} events`}
            </button>
            {/* Show list of events when expanded */}
            {expandedEventsUser === user.id && (
              <ul>
                {eventsField.map((event, index) => (
                  <li key={index}>{event}</li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <em>Ingen</em>
        )}
      </td>


    </tr>
  );
};

// Point values per event type
const POINT_VALUES = {
  'KL🥩': 4,
  'Vino🍷': 4,
  'KD🧠': 5,
  'Blåtur🌍': 7,
};

// --- Main Scoreboard Component ---
const Scoreboard = () => {
  const navigate = useNavigate();
  const [rawUsers, setRawUsers] = useState([]);
  const [users, setUsers] = useState({});
  const [eventsList, setEventsList] = useState([]);
  const [usersReady, setUsersReady] = useState(false);
  const [eventsReady, setEventsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTable, setActiveTable] = useState('awards');
  const [expandedEventsUser, setExpandedEventsUser] = useState(null);
  const [eventTypeFilter, setEventTypeFilter] = useState(null);
  const [chartPointMode, setChartPointMode] = useState('host');
  const [chartEventFilter, setChartEventFilter] = useState(null);
  const [chartYearFilter, setChartYearFilter] = useState(null);

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const db = getFirestore(app);
    const usersCollection = collection(db, 'users');
    const eventsCollection = collection(db, 'events');

    const unsubscribeUsers = onSnapshot(
      usersCollection,
      (usersSnapshot) => {
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setRawUsers(usersList);
        setUsersReady(true);
      },
      (error) => {
        console.error('Error listening to users:', error);
        setUsersReady(true);
      }
    );

    const unsubscribeEvents = onSnapshot(
      eventsCollection,
      (eventsSnapshot) => {
        const nextEventsList = eventsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setEventsList(nextEventsList);
        setEventsReady(true);
      },
      (error) => {
        console.error('Error listening to events:', error);
        setEventsReady(true);
      }
    );

    return () => {
      unsubscribeUsers();
      unsubscribeEvents();
    };
  }, []);

  useEffect(() => {
    if (!usersReady || !eventsReady) return;

    const usersByName = new Map(
      rawUsers.map((user) => [
        (user.name || '').trim().toLowerCase(),
        {
          ...user,
          points: 0,
          host_points: 0,
          attended_events: [],
          hosted_events: [],
        }
      ])
    );

    eventsList.forEach((event) => {
      const type = event.event_type;
      const pointValue = POINT_VALUES[type] || 0;
      const eventDetail = `${event.sted_konsept || 'Ukjent'}, ${type || 'Ukjent'}, ${event.Kvartal || 'Ukjent'}`;

      const uniqueHosts = Array.isArray(event.Vert)
        ? [...new Set(event.Vert.map((name) => (name || '').trim().toLowerCase()).filter(Boolean))]
        : [];
      uniqueHosts.forEach((hostName) => {
        const user = usersByName.get(hostName);
        if (!user) return;
        user.host_points += pointValue;
        user.hosted_events.push(eventDetail);
      });

      const uniqueParticipants = Array.isArray(event.Deltagelse)
        ? [...new Set(event.Deltagelse.map((name) => (name || '').trim().toLowerCase()).filter(Boolean))]
        : [];
      uniqueParticipants.forEach((participantName) => {
        const user = usersByName.get(participantName);
        if (!user) return;
        user.points += pointValue;
        user.attended_events.push(eventDetail);
      });
    });

    const normalizedUsers = Array.from(usersByName.values());

    const sortedByHostPoints = [...normalizedUsers].sort(
      (a, b) => (b.host_points || 0) - (a.host_points || 0)
    );
    const sortedByParticipantPoints = [...normalizedUsers].sort(
      (a, b) => (b.points || 0) - (a.points || 0)
    );

    setUsers({ sortedByHostPoints, sortedByParticipantPoints });
    setLoading(false);
  }, [rawUsers, eventsList, usersReady, eventsReady]);

  // Given a user and a filter, recalculate points and filter events
  const getFilteredUserData = useCallback((user, mode, filter, yearFilter) => {
    const isHost = mode === 'arrangør';
    const eventsField = isHost ? user.hosted_events : user.attended_events;

    if ((!filter && !yearFilter) || !Array.isArray(eventsField)) {
      if (!yearFilter) {
        return {
          points: isHost ? (user.host_points || 0) : (user.points || 0),
          events: eventsField || [],
        };
      }
    }

    // Each event string is "sted_konsept, event_type, Kvartal"
    const filtered = (eventsField || []).filter(e => {
      const parts = e.split(', ');
      if (filter && parts[1] !== filter) return false;
      if (yearFilter) {
        const kvartal = parts[2] || '';
        const year = kvartal.split(' ')[0];
        if (year !== yearFilter) return false;
      }
      return true;
    });

    const pointValue = filter ? (POINT_VALUES[filter] || 0) : null;
    let points;
    if (pointValue !== null) {
      points = filtered.length * pointValue;
    } else {
      // No type filter: sum points per event type
      points = filtered.reduce((sum, e) => {
        const type = e.split(', ')[1];
        return sum + (POINT_VALUES[type] || 0);
      }, 0);
    }
    return { points, events: filtered };
  }, []);

  const renderTable = (mode) => {
    const isHost = mode === 'arrangør';
    const title = isHost ? '👑Beste arrangør👑' : '🏅Beste deltaker🏅';
    const sourceList = isHost
      ? users.sortedByHostPoints
      : users.sortedByParticipantPoints;

    // Recalculate and re-sort when a filter is active
    const pointsList = sourceList
      .map(user => {
        const { points, events } = getFilteredUserData(user, mode, eventTypeFilter);
        return {
          ...user,
          [isHost ? 'host_points' : 'points']: points,
          [isHost ? 'hosted_events' : 'attended_events']: events,
        };
      })
      .sort((a, b) => {
        const field = isHost ? 'host_points' : 'points';
        return (b[field] || 0) - (a[field] || 0);
      });

    return (
      <div>
        <h2 className="scoreboard-title">{title}</h2>
        <p className="scoreboard-subtitle">🍷 4p · 🥩 4p · 🧠 5p · 🌍 7p</p>
        <div className="filter-chip-bar">
          <button
            className={`filter-chip${eventTypeFilter === null ? ' active' : ''}`}
            onClick={() => setEventTypeFilter(null)}
          >Alle</button>
          <button
            className={`filter-chip${eventTypeFilter === 'KL🥩' ? ' active' : ''}`}
            onClick={() => setEventTypeFilter('KL🥩')}
          >🥩 KL</button>
          <button
            className={`filter-chip${eventTypeFilter === 'Vino🍷' ? ' active' : ''}`}
            onClick={() => setEventTypeFilter('Vino🍷')}
          >🍷 Vino</button>
          <button
            className={`filter-chip${eventTypeFilter === 'Blåtur🌍' ? ' active' : ''}`}
            onClick={() => setEventTypeFilter('Blåtur🌍')}
          >🌍 Blåtur</button>
        </div>

        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Navn</th>
                <th>{isHost ? 'Vertpoeng' : 'Deltakerpoeng'}</th>
                <th>{isHost ? 'Arrangerte events' : 'Deltatte events'}</th>
              </tr>
            </thead>
            <tbody>
              {pointsList.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  eventsList={eventsList}
                  expandedEventsUser={expandedEventsUser}
                  setExpandedEventsUser={setExpandedEventsUser}
                  mode={mode}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const CHART_COLORS = ['#2d6a8a', '#3a87a8', '#4aa3c4', '#62b8d6', '#7dcde6', '#3577a0', '#5098b8', '#68b0cc', '#8ac4da', '#a2d8ea'];

  // Extract available years from all user events for the year dropdown
  const availableYears = useMemo(() => {
    const allUsers = users.sortedByHostPoints || [];
    const yearSet = new Set();
    allUsers.forEach(user => {
      const events = [...(user.hosted_events || []), ...(user.attended_events || [])];
      events.forEach(e => {
        const kvartal = (e.split(', ')[2] || '').split(' ')[0];
        if (kvartal) yearSet.add(kvartal);
      });
    });
    return [...yearSet].sort();
  }, [users]);

  const scoreboardChartData = useMemo(() => {
    const allUsers = users.sortedByHostPoints || [];
    const isHost = chartPointMode === 'host';

    return allUsers
      .map(user => {
        const { points } = getFilteredUserData(user, isHost ? 'arrangør' : 'deltaker', chartEventFilter, chartYearFilter);
        return { name: user.name, count: points };
      })
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [users, chartPointMode, chartEventFilter, chartYearFilter, getFilteredUserData]);

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="button-container">
        <button
          className={`button-17${activeTable === 'awards' ? '' : ' muted'}`}
          onClick={() => setActiveTable('awards')}
        >
          Oppsummering
        </button>

        <button
          className={`button-17${activeTable === 'diagram' ? '' : ' muted'}`}
          onClick={() => setActiveTable('diagram')}
        >
          Diagramvisning
        </button>

        <button
          className={`button-17${activeTable === 'arrangør' ? '' : ' muted'}`}
          onClick={() => setActiveTable('arrangør')}
        >
          Beste arrangør
        </button>

        <button
          className={`button-17${activeTable === 'deltaker' ? '' : ' muted'}`}
          onClick={() => setActiveTable('deltaker')}
        >
          Beste deltaker
        </button>

        <button
          className={`button-17${activeTable === 'klranking' ? '' : ' muted'}`}
          onClick={() => setActiveTable('klranking')}
        >
          KL-ranking
        </button>
      </div>

      {activeTable === 'arrangør' && renderTable('arrangør')}
      {activeTable === 'deltaker' && renderTable('deltaker')}
      {activeTable === 'klranking' && <KlRating />}
      {activeTable === 'awards' && <Utmerkelser />}
      {activeTable === 'diagram' && (
        <div className="chart-view-container">
          <div className="chart-mode-toggle">
            <button
              className={`filter-chip${chartPointMode === 'host' ? ' active' : ''}`}
              onClick={() => setChartPointMode('host')}
            >Vertpoeng</button>
            <button
              className={`filter-chip${chartPointMode === 'participant' ? ' active' : ''}`}
              onClick={() => setChartPointMode('participant')}
            >Deltakerpoeng</button>
          </div>
          <p className="scoreboard-subtitle">🍷 4p · 🥩 4p · 🧠 5p · 🌍 7p</p>
          <div className="filter-chip-bar">
            <button
              className={`filter-chip${chartEventFilter === null ? ' active' : ''}`}
              onClick={() => setChartEventFilter(null)}
            >Alle</button>
            <button
              className={`filter-chip${chartEventFilter === 'KL🥩' ? ' active' : ''}`}
              onClick={() => setChartEventFilter('KL🥩')}
            >🥩 KL</button>
            <button
              className={`filter-chip${chartEventFilter === 'Vino🍷' ? ' active' : ''}`}
              onClick={() => setChartEventFilter('Vino🍷')}
            >🍷 Vino</button>
            <button
              className={`filter-chip${chartEventFilter === 'Blåtur🌍' ? ' active' : ''}`}
              onClick={() => setChartEventFilter('Blåtur🌍')}
            >🌍 Blåtur</button>
          </div>
          <div className="chart-year-filter">
            <label htmlFor="chart-year-select">📅 År:</label>
            <select
              id="chart-year-select"
              className="chart-year-select"
              value={chartYearFilter || ''}
              onChange={(e) => setChartYearFilter(e.target.value || null)}
            >
              <option value="">Alle år</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="chart-wrapper" style={{ width: '100%', height: Math.max(300, scoreboardChartData.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={scoreboardChartData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
                  formatter={(value) => [value, 'Poeng']}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                  {scoreboardChartData.map((entry, index) => (
                    <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scoreboard;

